/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { css, keyframes } from '@emotion/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'

import { Button, buildGrainBackground } from '@slite/components'
import type { Icon } from '@slite/icons'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChatIcon,
  CheckMarkRoundIcon,
  CheckmarkIcon,
  ExpandTwoCornersIcon,
  FileIcon,
  GitHubIcon,
  LinearIcon,
  MinimizeIcon,
  PenIcon,
  SlackIcon,
  SliteAgentIcon,
  SliteIcon,
  ThumbsUpIcon,
} from '@slite/icons'

import { StreamingText } from '../../../components/StreamingText'
import { MEDIUM_BREAKPOINT } from '../../../utils/breakpoints'
import { useIsMobile } from '../../../utils/responsive'
import { AGENT_STEP_IDS, type AgentStepId, STEP_DURATION_MS } from './steps'

const EASE = [0.4, 0, 0.2, 1] as const

// `screen` blending keeps the grain from washing out the step color.
const GRAIN_BACKGROUND = buildGrainBackground({
  baseFrequency: 0.8,
  numOctaves: 4,
  exponent: 8,
  amplitude: 3,
  tileSize: 200,
})

// Seconds into a step: panel slide-ins and the approve pulse.
const SOURCES_IN = 1.6
const DOC_IN = 1.4
const APPROVE_AT = 1.2

// Chat shift while a side panel is open; some overlap is intentional.
const CHAT_PANEL_SHIFT = { x: -140, y: -20 }
const CHAT_PANEL_SHIFT_MOBILE = { x: -80, y: -20 }

const QUESTION =
  'Our support escalation process changed. Find what’s affected and update.'

// Typed into the composer late in the explain step, sent as draft opens.
const YES_REPLY = 'Yes'
const YES_TYPE_START_MS = 5600

const ACK_TEXT =
  'I’ll find the latest process, check it across your company sources and prepare the handbook changes for review.'

const TYPE_START_MS = 500
const TYPE_CHAR_MS = 30
const SEND_PAUSE_MS = 400
const SEND_AT_MS =
  TYPE_START_MS + QUESTION.length * TYPE_CHAR_MS + SEND_PAUSE_MS

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`

// Late in the explain step the citation underlines pulse, then the panel
// sources answer.
const CITATION_SHIMMER_S = STEP_DURATION_MS / 1000 - 3.5
const SOURCE_SHIMMER_S = CITATION_SHIMMER_S + 1

const citationPulse = keyframes`
  from, to { background-color: transparent; }
  25%, 30% { background-color: var(--alpha-yellow); }
`

const citationCss = css`
  border-bottom: 2px dotted var(--shade-40);
  border-radius: 4px 4px 0 0;
  animation: ${citationPulse} 1.4s ease-in-out ${CITATION_SHIMMER_S}s 1;
`

const sourceRowShimmerCss = css`
  animation: ${citationPulse} 1.4s ease-in-out ${SOURCE_SHIMMER_S}s 1;
`

// 15px matches the app's chat type ramp; no @slite/style token exists for it.
const planStepRowCss = css`
  font-size: 15px;
  line-height: 1.5em;
  overflow: hidden;
  min-width: 0;
`

const planStepIconCss = css`
  width: 16px;
  height: 16px;
  margin-right: 10px;
`

// Website-local replica of @slite/screens SuperPlanStepLayout.
function PlanStep({
  icon: StepIcon,
  label,
  durationMs,
  startDelayMs = 0,
}: {
  icon: Icon
  label: string
  durationMs: number
  startDelayMs?: number
}) {
  const [state, setState] = useState<'pending' | 'running' | 'done'>('pending')

  useEffect(() => {
    const start = setTimeout(() => setState('running'), startDelayMs)
    const finish = setTimeout(() => setState('done'), startDelayMs + durationMs)
    return () => {
      clearTimeout(start)
      clearTimeout(finish)
    }
  }, [durationMs, startDelayMs])

  const loading = state === 'running'
  const finished = state === 'done'
  const LeadingIcon = finished ? CheckmarkIcon : StepIcon

  return (
    <div>
      <div
        className={classNames(
          'flexbox flexbox-align-center shade-10',
          loading && 'font-weight-medium super-gradient animated'
        )}
        css={planStepRowCss}
      >
        <span
          className="flexbox flexbox-centered flexbox-no-shrink"
          css={planStepIconCss}
        >
          <LeadingIcon
            size={16}
            className={classNames(
              'block',
              loading && 'super-gradient-left-animated',
              finished && 'shade-40',
              !loading && !finished && 'shade-20'
            )}
          />
        </span>
        <span>{label}</span>
      </div>
    </div>
  )
}

const stageCss = css`
  position: relative;
  flex: 1 1 55%;
  min-width: 0;
  height: 640px;

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    flex: none;
    width: 100%;
    height: 540px;
    transform: scale(0.8) translateY(-60px) translateX(-10px);
  }
`

const circleCss = css`
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(112%, 680px);
  aspect-ratio: 1.4;
  border-radius: 50%;
  background-image: ${GRAIN_BACKGROUND};
  background-size: 200px 200px;
  background-repeat: repeat;
  background-blend-mode: screen;
`

const chatCss = css`
  position: absolute;
  top: 8px;
  left: 16%;
  right: 8px;
  bottom: 72px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    left: 8%;
    bottom: 56px;
  }
`

/* Overflow-hidden boxes still scroll programmatically: a ResizeObserver in
   AgentStage pins scrollTop to the bottom, clipping older messages first. */
const messagesCss = css`
  flex: 1;
  min-height: 0;
  padding: 6px 20px 12px;
  overflow: hidden;
  font-size: 14px;
  line-height: 1.55;
  color: var(--shade-05);
`

const messagesListCss = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const panelCss = css`
  position: absolute;
  top: 8px;
  bottom: 72px;
  right: 8px;
  width: 46%;
  z-index: 3;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    bottom: 56px;
    width: 60%;
  }
`

const stickerCss = css`
  position: absolute;
  z-index: 3;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
`

const miniFabCss = css`
  position: absolute;
  right: 8px;
  bottom: 0;
  z-index: 2;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    transform: translateY(16px);
  }
`

const hairline = '1px solid rgba(0, 0, 0, 0.06)'

// Block diff treatment matching the editor's HistoryDiffUI.
const diffBlockCss = css`
  position: relative;
  padding-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    bottom: 2px;
    width: 3px;
    border-radius: 2px;
  }

  &[data-diff='added']::before {
    background: var(--diff-added-block-bar-background);
  }

  &[data-diff='removed']::before {
    background: var(--diff-removed-block-bar-background);
  }

  &[data-diff='removed'] {
    text-decoration: line-through;
    text-decoration-color: var(--diff-removed-strikethrough-color);
    color: var(--shade-20);
  }
`

const STICKER_POSITIONS: CSSProperties[] = [
  { left: '10%', top: '1%', rotate: '-8deg' },
  { right: -10, top: '56%', rotate: '9deg' },
]

const STEP_STICKERS: Record<AgentStepId, ReactNode[]> = {
  ask: [<ChatIcon size={24} key="a" />, <SliteAgentIcon size={24} key="b" />],
  search: [<SlackIcon size={24} key="a" />, <LinearIcon size={24} key="b" />],
  explain: [<GitHubIcon size={24} key="a" />, <LinearIcon size={24} key="b" />],
  draft: [<PenIcon size={24} key="a" />, <FileIcon size={24} key="b" />],
  approve: [
    <CheckMarkRoundIcon size={24} key="a" />,
    <ThumbsUpIcon size={24} key="b" />,
  ],
}

function Appear({
  delay = 0,
  children,
}: {
  delay?: number
  children: ReactNode
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{
        opacity: 0,
        y: -10,
        transition: { duration: 0.25, ease: EASE },
      }}
      transition={{ duration: 0.35, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  )
}

const chatHeaderCss = css`
  padding: 12px 12px 10px 18px;
`

const headerIconBoxCss = css`
  width: 24px;
  height: 24px;
`

function ChatHeader() {
  return (
    <div
      className="flexbox flexbox-justify-end flexbox-align-center"
      css={chatHeaderCss}
    >
      <div className="flexbox flexbox-align-center flexbox-gap-8 shade-30">
        {[ExpandTwoCornersIcon, MinimizeIcon].map((HeaderIcon, i) => (
          <span
            key={i}
            className="flexbox flexbox-centered"
            css={headerIconBoxCss}
          >
            <HeaderIcon size={14} />
          </span>
        ))}
      </div>
    </div>
  )
}

const userBubbleCss = css`
  font-size: 15px;
  line-height: 1.5em;
  max-width: 88%;
  overflow-wrap: anywhere;
`

// Markup mirrors @slite/screens SuperQuestionLayout.
function UserBubble({ text }: { text: string }) {
  return (
    <div className="flexbox flexbox-align-center flexbox-justify-end">
      <div
        className="radius-xlarger background-shade-60 padding-horizontal-16 padding-vertical-12 shade-00 preserve-new-lines"
        css={userBubbleCss}
      >
        {text}
      </div>
    </div>
  )
}

function Checklist() {
  return (
    <div className="flexbox flexbox-column flexbox-gap-12">
      <Appear delay={1.2}>
        <PlanStep
          icon={SliteIcon}
          startDelayMs={1200}
          durationMs={1400}
          label="Searching Slite for the current escalation process"
        />
      </Appear>
      <Appear delay={1.45}>
        <PlanStep
          icon={SlackIcon}
          startDelayMs={1450}
          durationMs={2000}
          label="Reviewing the latest decision in #support-ops"
        />
      </Appear>
      <Appear delay={1.7}>
        <PlanStep
          icon={LinearIcon}
          startDelayMs={1700}
          durationMs={2600}
          label="Checking implementation status in Linear"
        />
      </Appear>
      <Appear delay={1.95}>
        <PlanStep
          icon={GitHubIcon}
          startDelayMs={1950}
          durationMs={3200}
          label="Verifying the shipped workflow in GitHub"
        />
      </Appear>
    </div>
  )
}

const workedDividerCss = css`
  border-top: ${hairline};
  padding-top: 10px;
`

function WorkedDivider() {
  return (
    <div className="font-size-large shade-30" css={workedDividerCss}>
      Worked for 1m 13s
    </div>
  )
}

// Resting style of the app's SourceReferenceV2 inline citation.
function Citation({ children }: { children: ReactNode }) {
  return <span css={citationCss}>{children}</span>
}

type AnswerSegment = { text: string; cite?: boolean; bold?: boolean }

const ANSWER_PARAGRAPHS: AnswerSegment[][] = [
  [
    { text: 'I checked the Support Handbook against ' },
    { text: 'the decision in #support-ops', cite: true },
    { text: ' and ' },
    { text: 'the merged workflow in GitHub', cite: true },
    { text: ': P1 incidents now escalate immediately via PagerDuty.' },
  ],
  [
    {
      text: 'The handbook still says to wait for Support Manager approval — that section is outdated. ',
    },
    { text: 'Should I update it?', bold: true },
  ],
]

const ANSWER_LENGTH = ANSWER_PARAGRAPHS.flat().reduce(
  (length, segment) => length + segment.text.length,
  0
)

// Fast enough that the full answer lands before the citation shimmer.
const ANSWER_STREAM_START_MS = 600
const ANSWER_STREAM_TICK_MS = 15
const ANSWER_STREAM_CHARS_PER_TICK = 3

function AnswerBlock({ stream, cited }: { stream: boolean; cited: boolean }) {
  const [charCount, setCharCount] = useState(stream ? 0 : ANSWER_LENGTH)

  useEffect(() => {
    if (!stream) {
      setCharCount(ANSWER_LENGTH)
      return
    }
    let interval: ReturnType<typeof setInterval> | undefined
    const start = setTimeout(() => {
      interval = setInterval(() => {
        setCharCount((count) => {
          if (count >= ANSWER_LENGTH) {
            if (interval) clearInterval(interval)
            return count
          }
          return count + ANSWER_STREAM_CHARS_PER_TICK
        })
      }, ANSWER_STREAM_TICK_MS)
    }, ANSWER_STREAM_START_MS)
    return () => {
      clearTimeout(start)
      if (interval) clearInterval(interval)
    }
  }, [stream])

  let offset = 0
  return (
    <div className="flexbox flexbox-column flexbox-gap-12">
      {ANSWER_PARAGRAPHS.map((segments, paragraphIdx) => {
        const paragraphStart = offset
        const nodes = segments.map((segment, segmentIdx) => {
          const visibleLength = Math.max(
            0,
            Math.min(segment.text.length, charCount - offset)
          )
          offset += segment.text.length
          const visibleText = segment.text.slice(0, visibleLength)
          if (segment.cite && cited) {
            return <Citation key={segmentIdx}>{visibleText}</Citation>
          }
          if (segment.bold) {
            return <strong key={segmentIdx}>{visibleText}</strong>
          }
          return <span key={segmentIdx}>{visibleText}</span>
        })
        return charCount > paragraphStart ? (
          <p key={paragraphIdx}>{nodes}</p>
        ) : null
      })}
    </div>
  )
}

const thumbsDownCss = css`
  transform: rotate(180deg);
`

const sourcesChipCss = css`
  background: #f6f4f2;
  border-radius: 8px;
  padding: 4px 10px;
`

function SourcesChipRow() {
  return (
    <div className="flexbox flexbox-align-center flexbox-gap-12 shade-30">
      <ThumbsUpIcon size={14} />
      <ThumbsUpIcon size={14} css={thumbsDownCss} />
      <div
        className="flexbox flexbox-align-center flexbox-gap-6 font-size-large shade-10"
        css={sourcesChipCss}
      >
        <SlackIcon size={13} />
        <LinearIcon size={13} />
        <GitHubIcon size={13} />
        <span>Based on 12 sources</span>
      </div>
    </div>
  )
}

// Inline diff run using the app's real diff classes from @slite/style.
function DiffText({
  kind,
  children,
}: {
  kind: 'addition' | 'deletion'
  children: ReactNode
}) {
  return <span className={`diff-${kind}`}>{children}</span>
}

const diffCardHeaderCss = css`
  padding: 8px 8px 8px 12px;
`

const diffCardTitleCss = css`
  min-width: 0;
`

const diffCardTitleIconCss = css`
  width: 20px;
  height: 20px;
`

const approveWrapCss = css`
  border-radius: 8px;
  display: inline-flex;
`

const diffCardPreviewCss = css`
  padding: 10px 12px;
  line-height: 1.55;
`

const diffCardFooterCss = css`
  padding: 6px 12px 8px;
`

// Website-local replica of @slite/screens ApprovalCardShell.
function ChatDiffCard({ highlightApprove }: { highlightApprove: boolean }) {
  return (
    <div className="flexbox flexbox-column background-shade-60 radius-larger overflow-hidden border-small border-shade-45">
      <div
        className="flexbox flexbox-align-center flexbox-gap-8"
        css={diffCardHeaderCss}
      >
        <div
          className="flexbox-grow flexbox flexbox-align-center flexbox-gap-8 shade-20 font-weight-semibold font-size-large"
          css={diffCardTitleCss}
        >
          <span
            className="flexbox flexbox-centered flexbox-no-shrink shade-30"
            css={diffCardTitleIconCss}
          >
            <PenIcon size={14} />
          </span>
          <span className="ellipsis" css={diffCardTitleCss}>
            🚨 Escalating P1 incidents
          </span>
        </div>
        <div className="flexbox flexbox-align-center flexbox-gap-4 flexbox-no-shrink">
          <Button variant="secondary" size="small">
            Reject
          </Button>
          <motion.span
            animate={
              highlightApprove
                ? {
                    boxShadow: [
                      '0 0 0 0px rgba(122, 90, 248, 0)',
                      '0 0 0 4px rgba(122, 90, 248, 0.35)',
                    ],
                    scale: [1, 0.94, 1],
                  }
                : { boxShadow: '0 0 0 0px rgba(122, 90, 248, 0)' }
            }
            transition={
              highlightApprove
                ? { delay: APPROVE_AT, duration: 0.4, ease: EASE }
                : { duration: 0.2 }
            }
            css={approveWrapCss}
          >
            <Button variant="secondary-purple" size="small">
              Approve
            </Button>
          </motion.span>
        </div>
      </div>
      <div className="background-shade-100 border-top-small border-shade-45">
        <div className="font-size-large" css={diffCardPreviewCss}>
          <p>
            <DiffText kind="deletion">
              Wait for the Support Manager to approve before escalating.
            </DiffText>{' '}
            <DiffText kind="addition">
              Create a <strong>PagerDuty incident immediately</strong> and
              notify the <strong>Incident Lead</strong>. Approval is not
              required.
            </DiffText>
          </p>
        </div>
        <div
          className="flexbox flexbox-align-center flexbox-gap-4 shade-30 font-size-large"
          css={diffCardFooterCss}
        >
          <ArrowDownIcon size={12} />
          <span>Show in editor</span>
        </div>
      </div>
    </div>
  )
}

function useTypewriter(
  text: string,
  active: boolean,
  startMs: number = TYPE_START_MS
) {
  const [charCount, setCharCount] = useState(0)

  useEffect(() => {
    setCharCount(0)
    if (!active) return
    let interval: ReturnType<typeof setInterval> | undefined
    const start = setTimeout(() => {
      interval = setInterval(() => {
        setCharCount((count) => {
          if (count >= text.length) {
            if (interval) clearInterval(interval)
            return count
          }
          return count + 1
        })
      }, TYPE_CHAR_MS)
    }, startMs)
    return () => {
      clearTimeout(start)
      if (interval) clearInterval(interval)
    }
  }, [text, active, startMs])

  return active ? text.slice(0, charCount) : ''
}

const inputBarCss = css`
  padding: 0 16px 16px;
`

const inputBoxCss = css`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 10px 12px;
`

const inputTextCss = css`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`

const sendCircleCss = css`
  width: 26px;
  height: 26px;
  border-radius: 50%;
`

function InputBar({ typedText }: { typedText: string }) {
  return (
    <div css={inputBarCss}>
      <div
        className="flexbox flexbox-space-between flexbox-align-center flexbox-gap-12"
        css={inputBoxCss}
      >
        {/* justify-end keeps the tail of the typed text in view. */}
        <span
          className={classNames(
            'flexbox flexbox-justify-end font-size-large',
            typedText ? 'shade-00' : 'shade-30'
          )}
          css={inputTextCss}
        >
          <span>
            {typedText || 'Ask Slite Agent…'}
            {typedText && (
              <span
                aria-hidden
                css={css`
                  display: inline-block;
                  width: 2px;
                  height: 0.95em;
                  vertical-align: -0.1em;
                  margin-left: 2px;
                  background: currentColor;
                  animation: ${caretBlink} 0.9s infinite;
                `}
              />
            )}
          </span>
        </span>
        <span
          className="flexbox flexbox-centered flexbox-no-shrink background-shade-00 white"
          css={sendCircleCss}
        >
          <ArrowUpIcon size={14} />
        </span>
      </div>
    </div>
  )
}

const hairlineBottomCss = css`
  border-bottom: ${hairline};
`

const panelHeaderCss = css`
  padding: 14px 16px;
  border-bottom: ${hairline};
`

const sourceTitleCss = css`
  line-height: 1.4;
`

const sourceIconCss = css`
  margin-top: 3px;
`

const sourceDescriptionCss = css`
  line-height: 1.55;
  margin-top: 6px;
`

const PANEL_SOURCES = [
  {
    icon: SlackIcon,
    title: '#support-ops · P1 escalation decision',
    description:
      'Thread where the team agreed to escalate P1 incidents immediately via PagerDuty and notify the Incident Lead — Support Manager approval dropped.',
  },
  {
    icon: GitHubIcon,
    title: 'support-tooling #482 · escalation flow',
    description:
      'Merged PR wiring the PagerDuty trigger into the on-call workflow. Confirms approval is no longer part of the escalation path.',
  },
]

function SourcesPanel() {
  return (
    <motion.div
      css={panelCss}
      initial={{ x: '70%', opacity: 0 }}
      animate={{ x: '40%', opacity: 1 }}
      exit={{
        x: '40%',
        opacity: 0,
        transition: { duration: 0.35, ease: EASE },
      }}
      transition={{ duration: 0.65, ease: EASE, delay: SOURCES_IN }}
    >
      <div
        className="font-weight-semibold font-size-large"
        css={panelHeaderCss}
      >
        Citations
      </div>
      {PANEL_SOURCES.map(({ icon: SourceIcon, title, description }) => (
        <div
          key={title}
          className="padding-vertical-12 padding-horizontal-16"
          css={[sourceRowShimmerCss, hairlineBottomCss]}
        >
          <div
            className="flexbox flexbox-align-start flexbox-gap-6 font-weight-semibold font-size-large"
            css={sourceTitleCss}
          >
            <SourceIcon
              size={13}
              className="flexbox-no-shrink"
              css={sourceIconCss}
            />
            <span>{title}</span>
          </div>
          <p className="font-size-large shade-20" css={sourceDescriptionCss}>
            {description}
          </p>
        </div>
      ))}
    </motion.div>
  )
}

const docBodyCss = css`
  flex: 1;
  min-height: 0;
  padding: 14px 16px;
  line-height: 1.55;
  overflow: hidden;
`

// 15px doc headings, same off-token size as the chat's message type.
const docHeadingCss = css`
  font-size: 15px;
`

const docFooterCss = css`
  border-top: ${hairline};
  padding: 8px 12px;
`

function DocPanel() {
  return (
    <motion.div
      css={panelCss}
      initial={{ x: '70%', opacity: 0 }}
      animate={{ x: '40%', opacity: 1 }}
      exit={{
        x: '40%',
        opacity: 0,
        transition: { duration: 0.35, ease: EASE },
      }}
      transition={{ duration: 0.65, ease: EASE, delay: DOC_IN }}
    >
      <div
        className="padding-vertical-12 padding-horizontal-16 font-size-large shade-10 ellipsis"
        css={hairlineBottomCss}
      >
        📕 Support Handbook&ensp;/&ensp;🚨 Escalating P1 incidents
      </div>
      <div
        className="flexbox flexbox-column flexbox-gap-10 font-size-large shade-05"
        css={docBodyCss}
      >
        <div className="font-weight-bold" css={docHeadingCss}>
          Start the escalation
        </div>
        <div>
          Confirm the incident meets the P1 criteria and record the affected
          customers and the impact.
        </div>
        <div className="padding-vertical-8">
          <div css={diffBlockCss} data-diff="removed">
            Wait for the Support Manager to approve before escalating.
          </div>
          <div css={diffBlockCss} data-diff="added">
            Create a PagerDuty incident immediately and notify the Incident
            Lead. Approval is not required.
          </div>
        </div>
        <div>
          Add material updates to the PagerDuty incident and
          #support-escalations until the incident is resolved.
        </div>
        <div className="font-weight-bold" css={docHeadingCss}>
          Ownership
        </div>
        <div>
          The Incident Lead owns the P1 response once the PagerDuty incident is
          created.
        </div>
      </div>
      <div
        className="flexbox flexbox-justify-end flexbox-align-center"
        css={docFooterCss}
      >
        <Button variant="secondary-purple" size="small">
          Approve changes
        </Button>
      </div>
    </motion.div>
  )
}

export function AgentStage({
  step,
  surfaceColor,
}: {
  step: AgentStepId
  surfaceColor: string
}) {
  const isMobile = useIsMobile()
  const chatPanelShift = isMobile ? CHAT_PANEL_SHIFT_MOBILE : CHAT_PANEL_SHIFT

  const messagesRef = useRef<HTMLDivElement>(null)
  const messagesListRef = useRef<HTMLDivElement>(null)

  // Pins the chat scroll to the newest message as content grows.
  useEffect(() => {
    const container = messagesRef.current
    const list = messagesListRef.current
    if (!container || !list) return
    const pinToBottom = () => {
      container.scrollTop = container.scrollHeight
    }
    const observer = new ResizeObserver(pinToBottom)
    observer.observe(list)
    return () => observer.disconnect()
  }, [])

  // reached('draft') = the draft step or any later one.
  const stepIndex = AGENT_STEP_IDS.indexOf(step)
  const reached = (id: AgentStepId) => stepIndex >= AGENT_STEP_IDS.indexOf(id)

  const panelOpen = step === 'explain' || step === 'draft'
  const panelDelay = step === 'explain' ? SOURCES_IN : DOC_IN

  const isAskStep = step === 'ask'
  // The question bubble shows once the ask-step typewriter finishes sending.
  const [sent, setSent] = useState(!isAskStep)

  useEffect(() => {
    if (step !== 'ask') {
      setSent(true)
      return
    }
    setSent(false)
    const timer = setTimeout(() => setSent(true), SEND_AT_MS)
    return () => clearTimeout(timer)
  }, [step])

  const typedQuestion = useTypewriter(QUESTION, isAskStep && !sent)
  const typedYes = useTypewriter(
    YES_REPLY,
    step === 'explain',
    YES_TYPE_START_MS
  )
  const typedText = isAskStep
    ? typedQuestion
    : step === 'explain'
      ? typedYes
      : ''

  return (
    // Decorative mock; the StepList column carries the real content.
    <div css={stageCss} aria-hidden>
      <AnimatePresence>
        <motion.div
          key={step}
          css={circleCss}
          style={{
            backgroundColor: surfaceColor,
            x: '-45%',
            y: '-60%',
            rotate: '-15deg',
          }}
          initial={{ scale: 0.55, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{
            scale: 1.08,
            opacity: 0,
            transition: { duration: 0.3, ease: EASE },
          }}
          transition={{ type: 'spring', stiffness: 190, damping: 24 }}
        />
      </AnimatePresence>

      <AnimatePresence>
        {!panelOpen &&
          STEP_STICKERS[step].map((icon, i) => (
            <motion.div
              key={`${step}-${i}`}
              css={stickerCss}
              style={STICKER_POSITIONS[i]}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{
                scale: 0.5,
                opacity: 0,
                transition: { duration: 0.2, ease: EASE },
              }}
              transition={{
                type: 'spring',
                stiffness: 320,
                damping: 20,
                delay: 0.25 + i * 0.12,
              }}
            >
              {icon}
            </motion.div>
          ))}
      </AnimatePresence>

      <motion.div
        css={chatCss}
        animate={{
          x: panelOpen ? chatPanelShift.x : 0,
          y: panelOpen ? chatPanelShift.y : 0,
        }}
        transition={{
          duration: 0.65,
          ease: EASE,
          delay: panelOpen ? panelDelay : 0,
        }}
      >
        <ChatHeader />
        <div css={messagesCss} ref={messagesRef}>
          <div css={messagesListCss} ref={messagesListRef}>
            <AnimatePresence>
              {sent && (
                <Appear key="question">
                  <UserBubble text={QUESTION} />
                </Appear>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {step === 'search' && (
                <Appear key="ack" delay={0.3}>
                  <p className="font-size-large padding-bottom-8">
                    <StreamingText text={ACK_TEXT} cadenceMs={9} />
                  </p>
                </Appear>
              )}
              {step === 'search' && (
                <Appear key="checklist" delay={0.6}>
                  <Checklist />
                </Appear>
              )}
              {reached('explain') && !isMobile && (
                <Appear key="worked" delay={step === 'explain' ? 0.3 : 0.15}>
                  <WorkedDivider />
                </Appear>
              )}
              {reached('explain') && (
                <Appear key="answer" delay={step === 'explain' ? 0.55 : 0.2}>
                  <AnswerBlock
                    stream={step === 'explain'}
                    cited={step === 'explain'}
                  />
                </Appear>
              )}
              {step === 'explain' && (
                <Appear key="chips" delay={0.95}>
                  <SourcesChipRow />
                </Appear>
              )}
              {reached('draft') && (
                <Appear key="yes" delay={step === 'draft' ? 0.2 : 0.1}>
                  <UserBubble text={YES_REPLY} />
                </Appear>
              )}
              {reached('draft') && (
                <Appear key="diff" delay={step === 'draft' ? 0.45 : 0.25}>
                  <ChatDiffCard highlightApprove={step === 'approve'} />
                </Appear>
              )}
            </AnimatePresence>
          </div>
        </div>
        <InputBar typedText={typedText} />
      </motion.div>

      <AnimatePresence>
        {step === 'explain' && <SourcesPanel key="sources" />}
        {step === 'draft' && <DocPanel key="doc" />}
      </AnimatePresence>

      <div css={miniFabCss}>
        <SliteAgentIcon size={24} />
      </div>
    </div>
  )
}
